import { useTranslation } from 'react-i18next';
import { motion, useReducedMotion } from 'motion/react';
import logo from '@/assets/imgs/header/logo.png';
import hero from '@/assets/imgs/about/hero.png';
import visionImage from '@/assets/imgs/about/vision.png';
import missionImage from '@/assets/imgs/about/mission.png';
import valuesImage from '@/assets/imgs/about/values.png';
import cardButtonCircle from '@/assets/imgs/about/card-button-circle.svg';
import cardButtonArrow from '@/assets/imgs/about/card-button-arrow.svg';
import ceoQuoteOpen from '@/assets/imgs/about/ceo-quote-open.svg';
import ceoQuoteClose from '@/assets/imgs/about/ceo-quote-close.svg';
import executivesBackground from '@/assets/imgs/about/executives-background.svg';
import shareholderMizuho from '@/assets/imgs/about/shareholder-mizuho.png';
import shareholderChange from '@/assets/imgs/about/shareholder-change.png';
import shareholderSagawa from '@/assets/imgs/about/shareholder-sagawa.png';
import shareholderHaneda from '@/assets/imgs/about/shareholder-haneda.png';
import shareholderJcb from '@/assets/imgs/about/shareholder-jcb.png';

const valueCards = [
  { key: 'vision', image: visionImage, english: 'Our Vision' },
  { key: 'mission', image: missionImage, english: 'Our Mission' },
  { key: 'values', image: valuesImage, english: 'Our Values' },
] as const;

const executives = [
  { key: 'chen' },
  { key: 'cheng' },
  { key: 'chang' },
  { key: 'sakuraba' },
  { key: 'vivian' },
  { key: 'yasuhara' },
  { key: 'yamada' },
] as const;

const shareholders = [
  { key: 'mizuho', image: shareholderMizuho, imageClass: 'h-[130px] w-[312px] object-cover', hasNote: false },
  { key: 'change', image: shareholderChange, imageClass: 'h-[113px] w-[270px] object-contain', hasNote: false },
  { key: 'sagawa', image: shareholderSagawa, imageClass: 'h-[117px] w-[280px] object-contain', hasNote: false },
  { key: 'haneda', image: shareholderHaneda, imageClass: 'h-[109px] w-[262px] object-contain', hasNote: true },
  { key: 'jcb', image: shareholderJcb, imageClass: 'h-[102px] w-[132px]', hasNote: true },
] as const;

function HeroSection() {
  const { t } = useTranslation();

  return (
    <section id="ceo-message" className="h-[500px] bg-white">
      <div className="mx-auto mt-[50px] grid h-[500px] w-[1800px] grid-cols-1 grid-rows-1 overflow-hidden rounded-[30px]">
        <img
          src={hero}
          alt=""
          className="block object-cover object-bottom col-start-1 row-start-1 size-full"
        />
        <h1 className="col-start-1 row-start-1 flex h-full w-full items-center justify-center text-center font-douyin text-[70px] font-bold leading-[1.5] text-white">
          {t('about.hero.title')}
        </h1>
      </div>
    </section>
  );
}

function CeoMessageSection() {
  const { t, i18n } = useTranslation();
  const language = i18n.resolvedLanguage ?? i18n.language;
  const isChinese = language.startsWith('zh');
  const isJapanese = language.startsWith('ja');

  return (
    <section className="relative mt-[50px] overflow-hidden bg-white" aria-labelledby="ceo-message-title">
      <div className="relative mx-auto w-[1920px] pb-[59px] pt-[102px]">
        <div
          aria-hidden="true"
          className="absolute bottom-[131px] left-1/2 top-[61px] w-[1535px] -translate-x-1/2 -rotate-[2.52deg] rounded-[30px] bg-[#ccefff]"
        />

        <article className="relative mx-auto w-[1504px] overflow-hidden rounded-[30px] bg-[linear-gradient(145deg,#eefaff_0%,#f9fdff_42%,#ffffff_76%)] pb-[142px] shadow-[0_12px_40px_rgba(0,160,233,0.04)]">
          <header className="flex items-start justify-between pl-[52px] pr-[91px] pt-[29px]">
            <h2
              id="ceo-message-title"
              className="font-expose text-[90px] font-black leading-[1.04] tracking-[-1.8px] text-[#00a0e9]"
            >
              {t('about.ceoMessage.title')}
            </h2>
            <span aria-hidden="true" className="mt-[24px] block h-[10px] w-[70px] bg-[#00a0e9]" />
          </header>

          <div className="relative mx-auto mt-[38px] w-[1320px]">
            <img
              src={ceoQuoteOpen}
              alt=""
              aria-hidden="true"
              className="pointer-events-none absolute left-[6px] top-[-36px] h-[67px] w-[81px] select-none"
            />

            <div
              className={`ceo-message-copy text-black ${
                isChinese ? 'text-[30px]' : isJapanese ? 'text-[27px]' : 'text-[22px]'
              }`}
            >
              <div className="ceo-message-copy__paragraphs">
                <p>{t('about.ceoMessage.paragraph1')}</p>
                <p>{t('about.ceoMessage.paragraph2')}</p>
                <p>{t('about.ceoMessage.paragraph3')}</p>
              </div>
              <p className="ceo-message-copy__signature">{t('about.ceoMessage.signature')}</p>
            </div>

            <img
              src={ceoQuoteClose}
              alt=""
              aria-hidden="true"
              className="pointer-events-none absolute -bottom-[81px] right-0 h-[67px] w-[81px] rotate-180 select-none"
            />
          </div>
        </article>
      </div>
    </section>
  );
}

function ValuesSection() {
  const { t, i18n } = useTranslation();
  const reduceMotion = useReducedMotion();
  const valueDescriptionSpacing = i18n.resolvedLanguage === 'en-US' ? 'mt-[16px] leading-[1.5]' : 'mt-[24px] leading-[1.6]';
  const valueDescriptionSize = i18n.resolvedLanguage === 'en-US' ? 'text-[22px]' : 'text-[26px]';
  const transition = {
    duration: reduceMotion ? 0.01 : 0.4,
    ease: [0.22, 1, 0.36, 1] as const,
  };

  return (
    <section
      id="vision"
      className="relative h-[1024px] overflow-hidden rounded-t-[64px] bg-[linear-gradient(173deg,#dff1ff_9%,#f5fbff_54%)]"
    >
      <div className="pt-[105px]">
        <h2 className="text-center font-douyin text-[50px] font-bold leading-[1.5] text-[#00a0e9]">
          {t('about.values.title')}
        </h2>
        <div className="mt-[87px] flex justify-center gap-[56px]">
          {valueCards.map(card => (
            <motion.article
              key={card.key}
              className="relative h-[620px] w-[440px] overflow-hidden rounded-[28px] outline-none"
              initial="rest"
              animate="rest"
              whileHover="active"
              whileFocus="active"
              tabIndex={0}
            >
              <img src={card.image} alt="" className="absolute inset-0 object-cover size-full" />

              <motion.div
                aria-hidden="true"
                className="absolute inset-0 bg-[linear-gradient(180deg,rgba(0,160,233,0)_22%,rgba(0,160,233,0.18)_36%,rgba(0,160,233,0.72)_62%,#2fa4e4_100%)]"
                style={{
                  WebkitMaskImage: `url(${card.image})`,
                  maskImage: `url(${card.image})`,
                  WebkitMaskPosition: 'center',
                  maskPosition: 'center',
                  WebkitMaskRepeat: 'no-repeat',
                  maskRepeat: 'no-repeat',
                  WebkitMaskSize: '100% 100%',
                  maskSize: '100% 100%',
                }}
                variants={{ rest: { opacity: 0 }, active: { opacity: 1 } }}
                transition={transition}
              />

              <motion.div
                className="absolute inset-x-[40px] top-[190px] text-white"
                variants={{ rest: { y: 160 }, active: { y: 0 } }}
                transition={transition}
              >
                <div className="flex flex-col items-center">
                  <h3 className="font-douyin text-[50px] font-bold leading-none">
                    {t(`about.values.${card.key}`)}
                  </h3>
                  <p className="mt-[12px] font-douyin text-[30px] font-bold leading-[1.5]">
                    {card.english}
                  </p>
                </div>
                <motion.p
                  className={`${valueDescriptionSpacing} ${valueDescriptionSize} text-left`}
                  variants={{
                    rest: { y: 40, opacity: 0 },
                    active: { y: 0, opacity: 1 },
                  }}
                  transition={transition}
                >
                  {t(`about.values.${card.key}Description`)}
                </motion.p>
              </motion.div>

              <div className="absolute bottom-0 left-1/2 h-[63px] w-[63px] -translate-x-1/2">
                <motion.img
                  src={cardButtonCircle}
                  alt=""
                  className="absolute inset-0 size-full"
                  variants={{
                    rest: { filter: 'hue-rotate(0deg) saturate(1) brightness(1)' },
                    active: {
                      filter: 'hue-rotate(174deg) saturate(1.65) brightness(1.12)',
                    },
                  }}
                  transition={transition}
                />
                <motion.img
                  src={cardButtonArrow}
                  alt=""
                  className="absolute inset-0 size-full"
                  variants={{ rest: { rotate: 0 }, active: { rotate: -180 } }}
                  transition={transition}
                />
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}

function CompanySection() {
  const { t, i18n } = useTranslation();
  const companyDescriptionSize = i18n.resolvedLanguage === 'en-US' ? 'text-[26px]' : 'text-[30px]';
  const shareholderNameSize = i18n.resolvedLanguage === 'zh-CN' ? 'text-[30px]' : 'text-[24px]';
  const companyRows = [
    { key: 'website', value: 'https://bilionpass.biz' },
    { key: 'founded', value: undefined },
    { key: 'address', value: undefined },
    { key: 'licenses', value: undefined },
    { key: 'phone', value: '03-6205-7178' },
    { key: 'fax', value: '03-6205-7179' },
  ] as const;

  return (
    <section id="profile" className="relative h-[2060px] overflow-hidden bg-white">
      <div className="relative mx-auto h-full w-[1400px] pt-[123px]">
        <div className="relative z-10 grid h-[330px] grid-cols-[560px_623px] justify-between">
          <div className="flex w-[560px] flex-col whitespace-nowrap font-expose text-[122px] font-black leading-[0.77] text-[#00a0e9]">
            <p>Japan DX</p>
            <p className="mt-[12px] self-end">SINCE</p>
            <p className="ml-[48px] mt-[12px]">2021.04</p>
          </div>
          <div className="mt-[10px] w-[623px]">
            <div className="flex items-center gap-[24px]">
              <h2 className="font-douyin text-[50px] font-bold leading-[1.5] text-[#00a0e9]">
                {t('about.company.name')}
              </h2>
            </div>
            <p className={`mt-[20px] ${companyDescriptionSize} leading-[1.7] text-[#355f72]`}>
              {t('about.company.description')}
            </p>
          </div>
        </div>

        <dl className="relative z-10 mt-[79px] border-y border-[#414141] text-[30px] leading-[1.5] text-[#414141]">
          {companyRows.map(({ key, value }) => (
            <div
              key={key}
              className={`grid grid-cols-[290px_1fr] items-center border-b border-[#d7d7d7] ${
                key === 'licenses' ? 'min-h-[160px]' : 'h-[120px]'
              }`}
            >
              <dt className="pl-[80px] font-semibold tracking-[5px]">
                {key === 'fax' ? 'FAX' : t(`about.company.labels.${key}`)}
              </dt>
              <dd
                className={`pl-[85px] ${key === 'website' ? 'lowercase' : ''} ${
                  key === 'licenses' ? 'whitespace-pre-line leading-[1.6]' : ''
                }`}
              >
                {value ?? t(`about.company.values.${key}`)}
              </dd>
            </div>
          ))}

          <div className="grid grid-cols-[290px_1fr] items-start pt-[47px] pb-[40px]">
            <dt className="pl-[80px] font-semibold">{t('about.company.labels.shareholders')}</dt>
            <dd className="-ml-[10px] grid w-[1140px] grid-cols-3 justify-between gap-y-[57px]">
              {shareholders.map(item => (
                <div key={item.key} className="flex w-[380px] flex-col items-center text-center">
                  <div className="flex h-[134px] w-[314px] items-center justify-center overflow-hidden">
                    <img src={item.image} alt="" className={item.imageClass} />
                  </div>
                  <p className={`mt-[14px] ${shareholderNameSize} leading-none whitespace-nowrap`}>
                    {t(`about.shareholders.${item.key}.name`)}
                  </p>
                  {item.hasNote && (
                    <p className="mt-[8px] text-[24px] leading-[1.25]">
                      {t(`about.shareholders.${item.key}.note`)}
                    </p>
                  )}
                </div>
              ))}
            </dd>
          </div>
        </dl>

        <img
          src={logo}
          alt=""
          aria-hidden="true"
          className="pointer-events-none absolute left-0 top-[1617px] z-0 h-[316px] w-[1400px] object-contain opacity-[0.05]"
        />
      </div>
    </section>
  );
}

function ExecutivesSection() {
  const { t } = useTranslation();

  return (
    <section
      id="members"
      className="relative h-[1800px] overflow-hidden"
      aria-labelledby="executives-title"
    >
      <img
        src={executivesBackground}
        alt=""
        aria-hidden="true"
        className="absolute inset-0 pointer-events-none size-full -scale-x-100"
      />
      <div className="relative z-10 mx-auto w-[1400px] pt-[78px]">
        <h2
          id="executives-title"
          className="text-center font-douyin text-[50px] font-bold leading-[1.5] text-[#00a0e9]"
        >
          {t('about.executives.title')}
        </h2>

        <div className="mt-[85px] grid grid-cols-3 items-stretch gap-[40px]">
          {executives.map(item => (
            <article key={item.key} className="flex flex-col gap-[40px] bg-white px-[24px] py-[32px]">
              <header className="flex flex-col gap-[10px] font-medium leading-[1.3]">
                <p className="text-[24px] text-[#355f72]">
                  {t(`about.executives.${item.key}.role`)}
                </p>
                <h3 className="text-[34px] text-[#00A0E9]" style={{fontWeight: 500}}>
                  {t(`about.executives.${item.key}.name`)}
                </h3>
              </header>
              <span aria-hidden="true" className="block h-[4px] w-[206px] bg-[#FF5D21]" />
              <p className="whitespace-pre-line text-[20px] leading-[1.5] text-[#355f72]">
                {t(`about.executives.${item.key}.description`)}
              </p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function About() {
  return (
    <div className="min-w-[1480px] overflow-hidden bg-white text-[#355f72]">
      <HeroSection />
      <CeoMessageSection />
      <ValuesSection />
      <CompanySection />
      <ExecutivesSection />
    </div>
  );
}
